import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, Facebook, MessageCircle, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

const WHATSAPP_NUMBER = "917259886651";
const WHATSAPP_DISPLAY = "+91 72598 86651";

const initialForm = { name: "", business: "", phone: "", email: "", city: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/enquiry", payload);
      toast.success("Enquiry sent! Our team will get back to you shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact KNOVAS Trading",
    url: "https://knovas.in/contact",
    about: {
      "@type": "Organization",
      name: "KNOVAS Trading",
      email: "knovastrading@gmail.com",
      telephone: "+917259886651",
    },
  };

  const inputClass =
    "w-full bg-white border border-[#E5E7EB] px-4 py-3 text-sm text-[#2B2B2B] focus:border-[#FF6A00] focus:outline-none focus:ring-1 focus:ring-[#FF6A00] transition-colors";
  const labelClass = "text-[10px] tracking-[0.3em] uppercase text-[#6B7280] block mb-2";

  return (
    <section data-testid="contact-page" className="bg-[#F9FAFB] min-h-screen">
      <Seo
        title="Contact KNOVAS · Become a Dealer | Kitchen & Wardrobe Hardware India"
        description="Get in touch with KNOVAS Trading to become a dealer or enquire about our premium modular kitchen and wardrobe hardware range. Call, WhatsApp or fill our dealer enquiry form."
        path="/contact"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <p className="text-xs tracking-[0.4em] uppercase text-[#FF6A00] mb-6">Contact · Dealer Enquiry</p>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-[#2B2B2B] leading-[0.9]"
            style={{ fontFamily: "'Anton', sans-serif", letterSpacing: "-0.01em" }}
          >
            Let's build<br />something strong.
          </h1>
          <p className="mt-8 max-w-2xl text-sm sm:text-base text-[#4B5563] leading-relaxed border-l-2 border-[#FF6A00] pl-6">
            Interested in stocking KNOVAS hardware or need product details for your project?
            Send us an enquiry and our team will reach out with catalogues, pricing and dealer terms.
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Enquiry Form */}
        <div className="lg:col-span-7">
          <h2 className="text-xs tracking-[0.3em] uppercase text-[#2B2B2B] font-bold border-l-2 border-[#FF6A00] pl-3 mb-8">
            Dealer Enquiry Form
          </h2>
          <form data-testid="dealer-enquiry-form" noValidate onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelClass} htmlFor="name">Name *</label>
                <input id="name" data-testid="enquiry-name" className={inputClass} value={form.name} onChange={update("name")} placeholder="Your full name" required />
              </div>
              <div>
                <label className={labelClass} htmlFor="business">Business / Firm</label>
                <input id="business" data-testid="enquiry-business" className={inputClass} value={form.business} onChange={update("business")} placeholder="Company or shop name" />
              </div>
              <div>
                <label className={labelClass} htmlFor="phone">Phone *</label>
                <input id="phone" data-testid="enquiry-phone" type="tel" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="Mobile / WhatsApp number" required />
              </div>
              <div>
                <label className={labelClass} htmlFor="email">Email</label>
                <input id="email" data-testid="enquiry-email" type="email" className={inputClass} value={form.email} onChange={update("email")} placeholder="you@example.com" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="city">City</label>
                <input id="city" data-testid="enquiry-city" className={inputClass} value={form.city} onChange={update("city")} placeholder="City / region" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="message">Message</label>
                <textarea id="message" data-testid="enquiry-message" rows={5} className={inputClass} value={form.message} onChange={update("message")} placeholder="Tell us what you're looking for — series, quantities, project details." />
              </div>
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-4 bg-[#FF6A00] hover:bg-[#E65C00] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                <>Send Enquiry <Send size={16} className="group-hover:translate-x-1 transition-transform" /></>
              )}
            </button>
          </form>
        </div>

        {/* Direct contact */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#E5E7EB] p-10">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[#2B2B2B] font-bold">Reach Us Directly</h2>
            <ul className="mt-8 space-y-7">
              <li className="flex items-start gap-4">
                <MessageCircle size={18} className="text-[#FF6A00] mt-1 shrink-0" />
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] block">WhatsApp</span>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi KNOVAS, I'd like to enquire about your hardware range.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    className="text-base text-[#2B2B2B] hover:text-[#FF6A00] transition-colors"
                  >
                    {WHATSAPP_DISPLAY}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={18} className="text-[#FF6A00] mt-1 shrink-0" />
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] block">Email</span>
                  <a href="mailto:knovastrading@gmail.com" data-testid="contact-email" className="text-base text-[#2B2B2B] hover:text-[#FF6A00] transition-colors">knovastrading@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Instagram size={18} className="text-[#FF6A00] mt-1 shrink-0" />
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] block">Instagram</span>
                  <a href="https://instagram.com/knovas_trading" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-base text-[#2B2B2B] hover:text-[#FF6A00] transition-colors">@knovas_trading</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={18} className="text-[#FF6A00] mt-1 shrink-0" />
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] block">Facebook</span>
                  <a href="https://www.facebook.com/share/1F9H6JGAZC/" target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="text-base text-[#2B2B2B] hover:text-[#FF6A00] transition-colors">KNOVAS on Facebook</a>
                </div>
              </li>
            </ul>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi KNOVAS, I'd like to enquire about your hardware range.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-cta"
              className="mt-10 inline-flex items-center gap-3 bg-[#2B2B2B] hover:bg-black text-white px-6 py-4 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
